import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useState } from 'react';
import Tag from '../../../../components/Display/Tag';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import { CONSTANT } from '../../../../utils/constants';
import ImageForm from '../../common/image/ImageForm';
import { validateURL } from '../../../../utils/stringHelper';
import PropertyField from '../../../../components/Properties/PropertyField';

function EventManager({
  isOpen,
  event,
  onUpdate,
  loading,
  onCreate,
  onClose,
  duplicationMode,
  highlightList
}) {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const [type, setType] = useState('');
  const [schedule, setSchedule] = useState('');
  const [place, setPlace] = useState('');
  const [price, setPrice] = useState('');
  const [thematique, setThematique] = useState('');
  const [highlight, setHighlight] = useState('');
  const [registrationUrl, setRegistrationUrl] = useState('');
  const [description, setDescription] = useState('');

  const [isDraft, setIsDraft] = useState(true);
  const [image, setImage] = useState(undefined);

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setStartDate(new Date(event.start_dt));
      setEndDate(new Date(event.end_dt));
      setPlace(event.place);
      setType(event.type);
      setPrice(event.price);
      setImage(event.image);
      setIsDraft(event.is_draft);
      setThematique(event.thematique?.id);
      setHighlight(event.highlight);
      setRegistrationUrl(event.registration_url);
      setDescription(event.description);
      setSchedule(event.schedule);
    } else {
      setTitle('');
      setStartDate(undefined);
      setEndDate(undefined);
      setPlace('');
      setType('');
      setPrice('');
      setImage(undefined);
      setIsDraft(true);
      setThematique('');
      setHighlight('');
      setRegistrationUrl('');
      setDescription('');
      setSchedule('');
    }
  }, [event, onCreate, onUpdate]);

  const handleValidate = () => {
    const eventPayload = {
      title,
      start_dt: startDate,
      end_dt: endDate,
      place,
      type,
      price,
      image: image?.id,
      is_draft: isDraft,
      thematique,
      highlight: highlight?.id,
      registration_url: registrationUrl,
      description,
      schedule
    };
    if (duplicationMode || !event) {
      onCreate(eventPayload);
    } else {
      onUpdate(eventPayload);
    }
  };

  const formIsValid = () => {
    return title.length > 3 && startDate && endDate && place && description.length > 3 && type;
  };

  const statusTemplate = (option) => <Tag tag={{ label: option.label, bgColor: option.bgColor }} />;

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider la ${!event || duplicationMode ? 'création' : 'modification'}`}
        className="w-full"
        onClick={handleValidate}
        severity="success"
        disabled={!formIsValid()}
        loading={loading}
      />
    </div>
  );

  return (
    <Sidebar
      style={{ width: 700 }}
      visible={isOpen}
      position="right"
      onHide={onClose}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${!event || duplicationMode ? 'Ajouter' : 'Modifier'} un Évènement`}</h3>
      }>
      <div className="sidebar-content-container">
        <div className="form-wrapper">
          <FormInput key="title" label={"Titre de l'évènement"} required isValid={title.length > 3}>
            <InputText
              id="title"
              value={title}
              maxLength={300}
              placeholder="Veuillez saisir le titre de l'évènement"
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormInput>

          <FormInput key="status" label={"Statut de l'évènement"}>
            <Dropdown
              value={isDraft}
              options={CONSTANT.shared.draftTypes}
              optionLabel="label"
              onChange={(e) => setIsDraft(e.value)}
              className="grow"
              valueTemplate={statusTemplate}
              itemTemplate={statusTemplate}
            />
          </FormInput>
          <div className="grid grid-cols-2 gap-2">
            <FormInput key="start_dt" label={'Date de début'} required isValid={startDate}>
              <Calendar
                id="start_dt"
                onChange={(e) => setStartDate(e.value)}
                placeholder="Selectionnez une date"
                value={startDate}
                locale="fr"
              />
            </FormInput>
            <FormInput key="end_dt" label={'Date de fin'} required isValid={endDate}>
              <Calendar
                id="end_dt"
                onChange={(e) => setEndDate(e.value)}
                placeholder="Selectionnez une date"
                value={endDate}
                locale="fr"
                minDate={startDate}
              />
            </FormInput>
          </div>
          <FormInput key="schedule" label={'Horaires'} helpMessage="Max. 50 caractères">
            <InputText
              id="schedule"
              value={schedule}
              maxLength={50}
              placeholder="Veuillez saisir les horaires de l'événement"
              onChange={(e) => setSchedule(e.target.value)}
            />
          </FormInput>

          <FormInput key="price" label={"Prix de l'événement"} helpMessage="Max. 50 caractères">
            <InputText
              id="price"
              value={price}
              maxLength={50}
              placeholder="Veuillez saisir le prix de l'événement"
              onChange={(e) => setPrice(e.target.value)}
            />
          </FormInput>

          <FormInput key="place" label={"Lieu de l'événement"} required isValid={place}>
            <Dropdown
              value={place}
              options={CONSTANT.quatrebis.place}
              optionLabel="label"
              onChange={(e) => setPlace(e.value)}
              placeholder="Sélectionner le lieu de l'évènement"
              className="grow"
            />
          </FormInput>

          <FormInput key="type" label={"Type d'événement"} required isValid={type}>
            <Dropdown
              value={type}
              options={CONSTANT.quatrebis.type}
              optionLabel="label"
              onChange={(e) => setType(e.value)}
              placeholder="Indiquez le type d'évènement"
              className="grow"
            />
          </FormInput>

          <FormInput key="thematique" label={'Thématique'} required isValid={thematique}>
            <PropertyField
              type={'qb_thematique'}
              selectedPropertyList={thematique}
              setSelectedPropertyList={setThematique}
              scope={'quatrebis'}
              isUniqueChoice
            />
          </FormInput>

          <FormInput key="highlights" label={'Temps forts'}>
            <Dropdown
              value={highlight}
              options={highlightList}
              optionLabel="title"
              onChange={(e) => setHighlight(e.value)}
              placeholder="Indiquez le temps de fort de l'évènement"
              className="grow"
            />
          </FormInput>

          <FormInput
            label={'Lien de réservation'}
            errorMessage={
              !validateURL(registrationUrl) && "L'URL doit commencer par http:// ou https://"
            }>
            <InputText
              id="registration_url"
              value={registrationUrl}
              maxLength={300}
              placeholder="Veuillez saisir l'url d'inscription"
              onChange={(e) => setRegistrationUrl(e.target.value)}
              invalid={!validateURL(registrationUrl)}
            />
          </FormInput>

          <FormInput
            key="description"
            label={'Description'}
            required
            isValid={description?.length > 3}>
            <RichTextEditor
              value={description}
              onChange={(value) => {
                setDescription(value);
              }}
              acceptHeaders
              acceptImage
            />
          </FormInput>
          <ImageForm
            image={image}
            setImage={setImage}
            deleteImage={() => setImage(undefined)}
            required={false}
          />
        </div>
        {footerButtons}
      </div>
    </Sidebar>
  );
}

export default EventManager;
